import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';

import { ApiKey, ApiKeyDelegate } from './api-keys.models';

export const API_KEY_STATUS_LABELS: Record<ApiKey['status'], string> = {
  active: 'Ativa',
  revoked: 'Revogada'
};

@Injectable({ providedIn: 'root' })
export class ApiKeysCsvService {
  private static readonly BOM = '\uFEFF';

  private static readonly SEPARATOR = ';';

  private static readonly EOL = '\r\n';

  private static readonly MIME_TYPE = 'text/csv;charset=utf-8;';

  private static readonly HEADER = [
    'Nome',
    'Client ID',
    'Final do Client Secret',
    'Situação',
    'Conta de serviço',
    'Criada por',
    'Criada em',
    'Revogada por',
    'Revogada em'
  ];

  private static readonly DELEGATES_HEADER = ['Usuário', 'Adicionado em'];

  private static readonly FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

  exportApiKeys(items: Array<ApiKey>, reference: Date = new Date()): string {
    const filename = this.apiKeysFilename(reference);

    this.download(this.buildApiKeysCsv(items), filename);

    return filename;
  }

  exportDelegates(
    holders: Array<ApiKeyDelegate>,
    owner: string,
    reference: Date = new Date()
  ): string {
    const filename = this.delegatesFilename(owner, reference);

    this.download(this.buildDelegatesCsv(holders), filename);

    return filename;
  }

  buildApiKeysCsv(items: Array<ApiKey>): string {
    const lines = [
      ApiKeysCsvService.HEADER.map(label => this.cell(label)).join(ApiKeysCsvService.SEPARATOR),
      ...(items ?? []).map(item => this.apiKeyRow(item))
    ];

    return this.document(lines);
  }

  buildDelegatesCsv(holders: Array<ApiKeyDelegate>): string {
    const lines = [
      ApiKeysCsvService.DELEGATES_HEADER.map(label => this.cell(label)).join(
        ApiKeysCsvService.SEPARATOR
      ),
      ...(holders ?? []).map(holder =>
        [this.cell(holder.username), this.cell(this.formatDateTime(holder.grantedAt))].join(
          ApiKeysCsvService.SEPARATOR
        )
      )
    ];

    return this.document(lines);
  }

  apiKeysFilename(reference: Date = new Date()): string {
    return `api-keys-${this.dateStamp(reference)}.csv`;
  }

  delegatesFilename(owner: string, reference: Date = new Date()): string {
    const safeOwner = (owner || '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const prefix = safeOwner ? `api-keys-acessos-${safeOwner}` : 'api-keys-acessos';

    return `${prefix}-${this.dateStamp(reference)}.csv`;
  }

  private download(content: string, filename: string): void {
    saveAs(new Blob([content], { type: ApiKeysCsvService.MIME_TYPE }), filename);
  }

  private document(lines: Array<string>): string {
    return `${ApiKeysCsvService.BOM}${lines.join(ApiKeysCsvService.EOL)}${ApiKeysCsvService.EOL}`;
  }

  private apiKeyRow(key: ApiKey): string {
    return [
      this.cell(key.name),
      this.cell(key.clientId),
      this.cell(key.maskedClientSecret),
      this.cell(API_KEY_STATUS_LABELS[key.status] ?? key.status),
      this.cell(key.owner || ''),
      this.cell(key.createdBy || ''),
      this.cell(this.formatDateTime(key.createdAt)),
      this.cell(key.revokedBy || ''),
      this.cell(this.formatDateTime(key.revokedAt))
    ].join(ApiKeysCsvService.SEPARATOR);
  }

  private cell(value: string | number | undefined | null): string {
    const text = String(value ?? '');
    const safe = ApiKeysCsvService.FORMULA_PREFIXES.some(prefix => text.startsWith(prefix))
      ? `'${text}`
      : text;

    return `"${safe.replace(/"/g, '""')}"`;
  }

  private formatDateTime(value: string | null | undefined): string {
    if (!value) { return ''; }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) { return value; }

    const day = `${this.pad(date.getDate())}/${this.pad(date.getMonth() + 1)}/${date.getFullYear()}`;

    return `${day} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
  }

  private dateStamp(reference: Date = new Date()): string {
    const date = Number.isNaN(reference?.getTime?.()) ? new Date() : reference;

    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
}
